import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import type { CustomerRow } from './easeedeskCustomers'

/**
 * Settings -> Licenses, calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/Licenses.php`:
 *
 *   companies_support / companiesSupportData
 *                              -> GET  /settings/licenses/companies
 *   companySupportlicenseForm  -> GET  /settings/licenses/companies/{id}
 *   updateCompaniesSupport     -> POST /settings/licenses/companies/{id}
 *   enable/disableCompaniesSupport
 *                              -> POST /settings/licenses/companies/{id}/license
 *   owners_support / ownersSupportData
 *                              -> GET  /settings/licenses/owners
 *   enable/disableOwnerSupport -> POST /settings/licenses/owners/{id}/license
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * Randurile filei de OAMENI sunt cele ale ecranului Customers - acelasi cap
 * vechi (`ownersSupportData`), deci acelasi tip (`CustomerRow`). Randurile
 * filei de FIRME poarta numele scrise de capatul nou (`companyName`,
 * `licenseTypeName`), nu numele de coloana din baza pe care le trimitea CI3.
 *
 * O salvare refuzata NU vine ca un 200 tacut, ca in portal: vine ca 400 cu
 * mesajul serverului, deci ajunge la ecran ca `ApiError`.
 */

/** O fila din bara. Fara `group`: bara are o singura sectiune. */
export type LicensesTab = {
  id: string
  label: string
  icon: string
  href: string
  right: string
  title?: string
}

export type LicensesShell = {
  pageTitle: string
  moduleUrl: string
  /** DEJA filtrate pe server: aici vin numai filele pe care omul are voie sa le vada. */
  tabs: LicensesTab[]
  /** `settings.licenses.edit`. Poarta ramane pe server; asta doar ascunde butoanele. */
  canWrite: boolean
  /** Firma aleasa in meniu - ale ei sunt contactele din fila de oameni. */
  company: number
}

export const TABS = ['companies_support', 'owners_support'] as const

export type LicensesTabId = (typeof TABS)[number]

export function isLicensesTab(value: string): value is LicensesTabId {
  return (TABS as readonly string[]).includes(value)
}

/** Raspunsul unei liste, in forma pe care o astepta DataTables. */
export type TablePage<T> = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: T[]
}

/** `Companies_support::getActions`: ce se poate face cu licenta randului. */
export type LicenseActions = {
  add: number
  disabled: number
  edit: number
}

/**
 * Un rand din fila de firme.
 *
 * `disabled` e `null` cand firma NU are licenta, si `0` cand are una pornita.
 * Cele doua nu se amesteca: pe ele se sprijina si insigna, si butoanele.
 */
export type CompanyLicenseRow = {
  companyId: number
  companyName: string
  licenseId: number | null
  licenseType: number | null
  licenseTypeName: string
  allUsers: number
  byDateRange: number
  startLicense: string
  endLicense: string
  disabled: number | null
  actions: LicenseActions
  DT_RowId?: string
}

/** Un tip de licenta de firma, din `it_company_support_license_type`. */
export type CompanyLicenseType = {
  id: number
  name: string
  allUsers: number
  byDateRange: number
}

export type CompanyLicenseForm = {
  companyId: number
  license: {
    licenseType: number | null
    startLicense: string
    endLicense: string
    disabled: number | null
  } | null
  licenseTypes: CompanyLicenseType[]
}

export function licensesShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'licenses', 'shell'],
    queryFn: () => apiGet<LicensesShell>('/settings/licenses'),
  })
}

export function companyLicensesQuery() {
  return queryOptions({
    queryKey: ['settings', 'licenses', 'companies'],
    queryFn: () => apiGet<TablePage<CompanyLicenseRow> & { ready: boolean }>('/settings/licenses/companies'),
  })
}

export function companyLicenseFormQuery(companyId: number) {
  return queryOptions({
    queryKey: ['settings', 'licenses', 'company-form', companyId],
    queryFn: () => apiGet<CompanyLicenseForm>(`/settings/licenses/companies/${companyId}`),
    enabled: companyId > 0,
  })
}

export function saveCompanyLicense(companyId: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; companyId: number }>(`/settings/licenses/companies/${companyId}`, fields)
}

/** "Disable license" / "Enable license" - cele doua capete vechi, intr-unul. */
export function setCompanyLicense(companyId: number, disabled: boolean) {
  return apiPost<{ saved: boolean }>(`/settings/licenses/companies/${companyId}/license`, { disabled: disabled ? 1 : 0 })
}

export function ownerLicensesQuery() {
  return queryOptions({
    queryKey: ['settings', 'licenses', 'owners'],
    queryFn: () => apiGet<TablePage<CustomerRow>>('/settings/licenses/owners'),
  })
}

/**
 * Formularul "Add / Edit license" al unui contact -
 * `ownerSupportLicenseForm`: licenta scrisa si tipurile (cu SKU) ale firmei
 * alese. `period` e `data-period` (zilele SKU-ului; -1 = nelimitat).
 */
export type OwnerLicenseForm = {
  owner: number
  license: { license_type: number; start_license: string; end_license: string } | null
  today: string
  types: {
    id: number
    name: string
    skuName: string
    cost: string
    currency: string
    recurringCost: string
    recurringCurrency: string
    period: number
    byDateRange: number
  }[]
}

/** Raspunsul lui `updateOwnerSupport`: licenta scrisa si ce a iesit din fiecare actiune a tipului. */
export type OwnerLicenseSaved = {
  saved: boolean
  ownerId: number
  actions: { sku: string; ticket: number; error: string }[]
}

export function ownerLicenseFormQuery(ownerId: number) {
  return queryOptions({
    queryKey: ['settings', 'licenses', 'owner-form', ownerId],
    queryFn: () => apiGet<OwnerLicenseForm>(`/settings/licenses/owners/${ownerId}`),
    enabled: ownerId > 0,
  })
}

export function saveOwnerLicense(ownerId: number, fields: Record<string, PostValue>) {
  return apiPost<OwnerLicenseSaved>(`/settings/licenses/owners/${ownerId}`, fields)
}

export function setOwnerLicense(ownerId: number, disabled: boolean) {
  return apiPost<{ saved: boolean }>(`/settings/licenses/owners/${ownerId}/license`, { disabled: disabled ? 1 : 0 })
}

export type { CustomerRow }
